import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStudent } from "../contexts/StudentContext";
import { usePremium } from "../contexts/PremiumContext";
import { xpForLevel } from "../services/progressService";
import ProgressBar from "./ProgressBar";

interface Item {
  key: string;
  icon: string;
  to: string;
  match: RegExp;
}

const STUDENT_ITEMS: Item[] = [
  { key: "home", icon: "fa-house", to: "/home/dashboard", match: /^\/home\/(dashboard)?$/ },
  { key: "subjects", icon: "fa-book-open", to: "/home/subjects", match: /^\/home\/(subjects|subject|chapter)/ },
  { key: "progress", icon: "fa-chart-line", to: "/home/progress", match: /^\/home\/(progress|badges)/ },
  { key: "profile", icon: "fa-user", to: "/home/profile", match: /^\/home\/(profile|edit-profile|account|premium|parent-access|notifications)/ },
];

/** Compte parent : « Mes enfants » + profil, sans XP ni Premium. */
const PARENT_ITEMS: Item[] = [
  { key: "children", icon: "fa-children", to: "/home/parent", match: /^\/home\/(parent(\/|$))?$/ },
  { key: "profile", icon: "fa-user", to: "/home/profile", match: /^\/home\/(profile|edit-profile|account)/ },
];

/** Navigation latérale (grand écran) — remplace la barre d'onglets mobile. */
const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { profile, stats, isParent } = useStudent();
  const { isPremium } = usePremium();
  const next = xpForLevel(stats.level + 1);
  const prev = xpForLevel(stats.level);
  const pct = ((stats.xp - prev) / Math.max(1, next - prev)) * 100;
  const items = isParent ? PARENT_ITEMS : STUDENT_ITEMS;
  const homePath = isParent ? "/home/parent" : "/home/dashboard";

  return (
    <aside className="sidebar">
      <button type="button" className="sidebar-brand" onClick={() => history.push(homePath)}>
        <span className="sidebar-logo">
          <i className="fa-solid fa-graduation-cap" />
        </span>
        <span>
          <span className="block text-lg font-black text-white leading-tight">Scholeo</span>
          <span className="block text-[11px] text-gray-400">{t("brand.tagline")}</span>
        </span>
      </button>

      <nav className="sidebar-nav">
        {items.map((it) => {
          const active = it.match.test(location.pathname);
          return (
            <button key={it.key} type="button" onClick={() => history.push(it.to)} className={`sidebar-item ${active ? "active" : ""}`}>
              <i className={`fa-solid ${it.icon}`} />
              <span>{t(`tabs.${it.key}`)}</span>
            </button>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        {!isPremium && !isParent && (
          <button type="button" onClick={() => history.push("/home/premium")} className="sidebar-premium">
            <i className="fa-solid fa-gem" />
            <span>{t("profile.premiumCta")}</span>
          </button>
        )}
        {profile && (
          <button type="button" onClick={() => history.push("/home/profile")} className="sidebar-user">
            <span className="text-2xl">{profile.avatar || "🎓"}</span>
            <span className="flex-1 min-w-0 text-left">
              <span className="block text-sm font-bold text-white truncate">{profile.displayName}</span>
              {isParent ? (
                <span className="block text-[11px] text-gray-400">{t("profile.parentRole")}</span>
              ) : (
                <>
                  <span className="block text-[11px] text-gray-400">
                    {t("dashboard.level", { level: stats.level })} · {stats.xp} XP
                  </span>
                  <ProgressBar value={pct} height={4} className="mt-1" />
                </>
              )}
            </span>
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
